import type { AtlPagesParams } from "@common/api/atl"
import type { AtlPageDetailPublic } from "@common/client"
import { UserInitials } from "@common/components/shared/user-initials"
import { formatDecimal, formatFlightHours } from "@common/lib/number"
import { createElement } from "react"
import { z } from "zod"
import type { ColumnSizing } from "@/lib/column-widths"
import type { GroupLabel } from "@/lib/grouping"
import {
  type Direction,
  directionSchema,
  withFixed,
} from "@/routes/_layout/maintenance-programmes_/$programmeId/-lib/table"
import { personName } from "@/routes/_layout/settings/_panel/-lib/history"

export type AtlPage = AtlPageDetailPublic

export const TECH_LOG_COLUMNS = [
  "page",
  "date",
  "route",
  "sectors",
  "landings",
  "hours",
  "defects",
  "fuel",
  "attachments",
  "created",
  "updated",
] as const

export const techLogColumnSchema = z.enum(TECH_LOG_COLUMNS)

export type TechLogColumn = z.infer<typeof techLogColumnSchema>

export type TechLogSlot = TechLogColumn | "actions"

export const TECH_LOG_COLUMN_LABELS: Record<TechLogColumn, string> = {
  page: "Page serial no.",
  date: "Date",
  route: "Route",
  sectors: "Sectors",
  landings: "Landings",
  hours: "Hours",
  defects: "Defects",
  fuel: "Fuel remaining",
  attachments: "Attachments",
  created: "Created",
  updated: "Updated",
}

export const TECH_LOG_SIZING: ColumnSizing<TechLogSlot> = {
  id: "aircraft-tech-log",
  widths: {
    page: 8,
    date: 5.5,
    route: 12,
    sectors: 4.5,
    landings: 5,
    hours: 4.5,
    defects: 4.5,
    fuel: 6.5,
    attachments: 6,
    created: 6.5,
    updated: 6.5,
    actions: 3.25,
  },
  fill: "route",
  pinned: ["actions"],
  extras: ["actions"],
  dropOrder: [
    "fuel",
    "attachments",
    "updated",
    "created",
    "route",
    "hours",
    "landings",
    "sectors",
    "defects",
  ],
}

export const TECH_LOG_SORTS = ["date", "page"] as const

export const techLogSortSchema = z.enum(TECH_LOG_SORTS)

export type TechLogSort = z.infer<typeof techLogSortSchema>

export const TECH_LOG_GROUPINGS = ["entered_by"] as const

export const techLogGroupingSchema = z.enum(TECH_LOG_GROUPINGS)

export type TechLogGrouping = z.infer<typeof techLogGroupingSchema>

export const TECH_LOG_GROUPING_LABELS: Record<TechLogGrouping, string> = {
  entered_by: "Entered by",
}

export const PAGE_TABS = [
  "legs",
  "defects",
  "parts",
  "servicing",
  "attachments",
] as const

export const pageTabSchema = z.enum(PAGE_TABS)

export type PageTab = z.infer<typeof pageTabSchema>

export const scanViewSchema = z.enum(["whole", "follow"])

export type ScanView = z.infer<typeof scanViewSchema>

export const techLogSearchFields = {
  log_search: z.string().optional().catch(undefined),
  log_sort: techLogSortSchema.optional().catch(undefined),
  log_direction: directionSchema.optional().catch(undefined),
  log_columns: z.array(techLogColumnSchema).optional().catch(undefined),
  log_group: techLogGroupingSchema.optional().catch(undefined),
  log_entered_by: z.array(z.string()).optional().catch(undefined),
  log_updated_by: z.array(z.string()).optional().catch(undefined),
  log_tab: pageTabSchema.optional().catch(undefined),
  log_scan: z.string().optional().catch(undefined),
  log_view: scanViewSchema.optional().catch(undefined),
  wide: z.boolean().optional().catch(undefined),
}

const techLogSearchSchema = z.object(techLogSearchFields)

export type TechLogSearch = z.infer<typeof techLogSearchSchema>

export type TechLogFilterValues = Pick<
  TechLogSearch,
  "log_entered_by" | "log_updated_by"
>

export const NO_TECH_LOG_FILTERS: TechLogFilterValues = {
  log_entered_by: undefined,
  log_updated_by: undefined,
}

export function hasTechLogFilters(values: TechLogFilterValues): boolean {
  return Boolean(values.log_entered_by?.length || values.log_updated_by?.length)
}

export type TechLogDisplay = {
  columns: TechLogColumn[]
  sort: TechLogSort
  direction: Direction
  group: TechLogGrouping | undefined
}

export function techLogDisplayOf(search: TechLogSearch): TechLogDisplay {
  return {
    columns: search.log_columns
      ? withFixed(TECH_LOG_COLUMNS, "page", search.log_columns)
      : [...TECH_LOG_COLUMNS],
    sort: search.log_sort ?? "date",
    direction: search.log_direction ?? "desc",
    group: search.log_group,
  }
}

export function techLogSlotsOf(columns: TechLogColumn[]): TechLogSlot[] {
  return [...columns, "actions"]
}

export function techLogPagesParams(
  aircraftId: string,
  search: TechLogSearch,
): AtlPagesParams {
  const display = techLogDisplayOf(search)
  return {
    aircraft_id: [aircraftId],
    status: ["recorded"],
    created_by_id: search.log_entered_by,
    last_modified_by_id: search.log_updated_by,
    search: search.log_search,
    sort: display.sort === "page" ? "log_number" : "flight_date",
    direction: display.direction,
    group: display.group,
  }
}

export function nextTechLogSort(
  display: TechLogDisplay,
  column: TechLogSort,
): Pick<TechLogSearch, "log_sort" | "log_direction"> {
  const direction =
    column === display.sort && display.direction === "desc" ? "asc" : "desc"
  return column === "date" && direction === "desc"
    ? { log_sort: undefined, log_direction: undefined }
    : { log_sort: column, log_direction: direction }
}

export function enteredByGroup(page: AtlPage): GroupLabel {
  const person = page.created_by
  return person
    ? {
        key: person.id,
        label: personName(person),
        icon: createElement(UserInitials, { user: person }),
      }
    : { key: "", label: "Unknown", order: 1 }
}

export function routeText(page: AtlPage): string {
  const [first] = page.sectors
  const stops = first
    ? [first.from_station, ...page.sectors.map((sector) => sector.to_station)]
    : []
  return stops.map((stop) => stop ?? "····").join(" → ")
}

export function blockMinutes(page: AtlPage): number {
  return page.sectors.reduce(
    (total, sector) => total + (sector.block_minutes ?? 0),
    0,
  )
}

export function landings(page: AtlPage): number {
  return page.sectors.reduce(
    (total, sector) => total + (sector.landings ?? 0),
    0,
  )
}

export function fuelRemaining(page: AtlPage): string | null {
  return page.sectors.at(-1)?.ops?.fuel_arrival_kg ?? null
}

export function hoursText(minutes: number): string {
  return formatFlightHours(minutes / 60)
}

export function kilogramsText(value: string | number): string {
  return `${formatDecimal(value, 2)} kg`
}
